import { Box, IconButton, Stack, Typography } from '@mui/material'
import { IconCross } from 'assets/icons'
import { Button } from 'elements/inputs/button'
import { useTranslation } from 'react-i18next'

export type DialogContentProps = {
  title?: string
  onConfirm?: () => void
  onDeny?: () => void
  confirmText?: string
  denyText?: string
  variant?: 'success' | 'danger'
  externalFormId?: string
  isConfirmDisabled?: boolean
  isActionLoading?: boolean
  children?: React.ReactNode
  hideCrossIcon?: boolean
  centerTitle?: boolean
  hideCancelButton?: boolean

  extraConfirmButtonText?: string
  extraConfirmButtonAction?: () => void

  confirmButtonIcon?: React.ReactNode
  hideButtons?: boolean
}

export const DialogContent = ({
  title,
  denyText,
  onDeny,
  externalFormId,
  variant = 'success',
  isConfirmDisabled = false,
  confirmText,
  onConfirm,
  children,
  isActionLoading = false,
  extraConfirmButtonText,
  extraConfirmButtonAction,
  centerTitle,
  hideCrossIcon,
  hideCancelButton,
  confirmButtonIcon,
  hideButtons,
}: DialogContentProps) => {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: 3, wordBreak: 'break-all' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1}
      >
        <Typography
          variant="body3"
          sx={{
            fontWeight: 600,
            textAlign: centerTitle ? 'center' : 'start',
          }}
        >
          {title}
        </Typography>

        {!hideCrossIcon && (
          <IconButton onClick={onDeny} sx={{ mt: 0.5, ml: 1 }}>
            <IconCross />
          </IconButton>
        )}
      </Box>

      {children}

      {(onDeny || onConfirm) && !hideButtons && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent={hideCancelButton ? 'flex-end' : 'space-between'}
          mt={4}
        >
          {onDeny && !hideCancelButton && (
            <Button variant="outlined" onClick={onDeny}>
              {denyText ?? t('common.cancel')}
            </Button>
          )}

          <Stack direction="row" spacing={3}>
            {extraConfirmButtonText && (
              <Button
                type={externalFormId ? 'submit' : 'button'}
                disabled={isConfirmDisabled}
                variant="outlined"
                form={externalFormId}
                loading={isActionLoading}
                onClick={extraConfirmButtonAction}
              >
                {extraConfirmButtonText}
              </Button>
            )}

            {(onConfirm || externalFormId) && (
              <Button
                type={externalFormId ? 'submit' : 'button'}
                color={variant === 'danger' ? 'error' : 'primary'}
                onClick={onConfirm}
                disabled={isConfirmDisabled}
                loading={isActionLoading}
                form={externalFormId}
                startIcon={confirmButtonIcon}
              >
                {confirmText ?? t('common.confirm')}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  )
}
